import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { FileDown, Upload } from "lucide-react";
import { downloadBlob, errMsg, http } from "../../lib/api";
import { can, useAuth } from "../../lib/auth";
import { Btn, Card } from "../kit/Kit";
import { AsigForm, mxn, nombreDe, sel, useCatalogos } from "./common";
import { Ficha } from "./Ficha";
import { useCopilotoFiltros } from "../../lib/copiloto";

export const CapAhorradores = ({ sinAsignar = false }) => {
  const { user } = useAuth();
  const c = useCatalogos();
  const [q, setQ] = useState("");
  useCopilotoFiltros(sinAsignar ? "captacion_sin_asignar" : "captacion_ahorradores", (p) => setQ(p === null ? "" : p.q ?? q), { filtros: { q }, vista: sinAsignar ? "Captación · Sin asignar" : "Captación · Ahorradores" });
  const [rows, setRows] = useState([]);
  const [chk, setChk] = useState([]);
  const [ficha, setFicha] = useState(null);
  const load = useCallback(() => http.get("/captacion/ahorradores", { params: { q, sin_asignar: sinAsignar } }).then((r) => setRows(r.data)).catch(() => setRows([])), [q, sinAsignar]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);
  const asigna = can(user, "captacion.asignar");
  const tog = (id) => setChk(chk.includes(id) ? chk.filter((x) => x !== id) : [...chk, id]);
  return (
    <div className="space-y-4" data-testid={sinAsignar ? "cap-sin-asignar" : "cap-ahorradores"}>
      {sinAsignar && asigna && <Sugerencias onDone={load} />}
      <Card title={`${sinAsignar ? "Sin asignar" : "Ahorradores"} (${rows.length})`} right={<input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar" className={`${sel} w-44`} data-testid="cap-ah-q" />}>
        {asigna && chk.length > 0 && <div className="mb-3 rounded-xl border border-[#E2DDD5] bg-[#FAF8F5] p-3" data-testid="cap-masivo"><p className="mb-2 text-xs font-semibold">Asignación masiva · {chk.length} seleccionados</p>
          <AsigForm ids={chk} onDone={() => { setChk([]); load(); }} testid="cap-masivo-form" /></div>}
        {rows.map((a) => (
          <div key={a.id} className="flex items-center gap-3 border-t border-[#F2F0EC] py-2.5" data-testid="cap-ah-row">
            {asigna && <input type="checkbox" checked={chk.includes(a.id)} onChange={() => tog(a.id)} data-testid={`cap-ah-chk-${a.pisa_id}`} aria-label="Seleccionar" />}
            <button onClick={() => setFicha(a.id)} className="grid min-w-0 flex-1 grid-cols-[1fr_auto] gap-x-3 text-left md:grid-cols-[1.5fr_1fr_1fr_auto]">
              <span className="truncate text-sm font-medium">{a.nombre || a.pisa_id}</span>
              <span className="text-right text-sm font-semibold md:order-last">{mxn(a.saldo)}</span>
              <span className="truncate text-xs text-[#5C5850]">{a.tipo || "—"} · PISA {a.pisa_id} · {a.rfc || "sin RFC"}</span>
              <span className="truncate text-xs">{nombreDe(c, a.referenciador_id) || nombreDe(c, a.socio_id) || <b className="text-rose-700">sin asignar</b>}</span>
            </button>
          </div>))}
      </Card>
      <Ficha id={ficha} onClose={() => setFicha(null)} onSaved={load} />
    </div>
  );
};

const Sugerencias = ({ onDone }) => {
  const [s, setS] = useState([]);
  const load = () => http.get("/captacion/sugerencias").then((r) => setS(r.data)).catch(() => setS([]));
  useEffect(() => { load(); }, []);
  const confirmar = async (items) => {
    try { await http.post("/captacion/sugerencias/confirmar", { items }); toast.success(`${items.length} asignaciones confirmadas`); load(); onDone(); } catch (e) { toast.error(errMsg(e)); }
  };
  const subir = async (e) => {
    const fd = new FormData();
    fd.append("file", e.target.files[0]);
    try { const r = await http.post("/captacion/asignacion/excel", fd); toast.success(`${r.data.asignados} asignados · ${r.data.errores.length} errores`); onDone(); } catch (er) { toast.error(errMsg(er)); }
    e.target.value = "";
  };
  return (
    <Card title={`Sugerencias (${s.length})`} subtitle="Coincidencias por RFC o nombre en el historial de comisiones. Nada se asigna sin tu confirmación."
      right={<div className="flex flex-wrap gap-2"><Btn kind="secondary" onClick={() => downloadBlob("/captacion/asignacion/plantilla", "Plantilla_asignacion_captacion.xlsx")} data-testid="cap-plantilla"><FileDown size={14} />Plantilla</Btn>
        <label className="inline-flex cursor-pointer items-center gap-1.5 rounded-full border border-[#E2DDD5] px-4 py-2 text-sm" data-testid="cap-excel-label"><Upload size={14} />Subir Excel<input type="file" accept=".xlsx" onChange={subir} className="hidden" data-testid="cap-excel" /></label></div>}>
      {s.map((x) => (
        <div key={x.ahorrador_id} className="flex flex-wrap items-center justify-between gap-2 border-t border-[#F2F0EC] py-2 text-sm" data-testid="cap-sug-row">
          <span className="min-w-0">{x.nombre || x.pisa_id} → <b>{x.referenciador}</b> <span className="text-xs text-[#8C867A]">({x.razon}, {Math.round(x.score * 100)}%)</span></span>
          <Btn kind="secondary" className="px-3 py-1 text-xs" onClick={() => confirmar([x])} data-testid={`cap-sug-ok-${x.pisa_id}`}>Confirmar</Btn>
        </div>))}
      {s.length > 1 && <Btn className="mt-3" onClick={() => confirmar(s)} data-testid="cap-sug-todas">Confirmar todas</Btn>}
    </Card>
  );
};
